import type { BillingUsage, GlobalBillingUsage } from '@/types';

const CATEGORY_LABELS: Record<string, string> = {
  image: '图片',
  video: '视频',
  text: '文本',
  unknown: '未知',
};

const POINTS_PER_YUAN = 5;

export interface BillingSummaryItem {
  label: string;
  value: string;
  caption: string;
  emphasized?: boolean;
}

export type BillingDatePreset = 'all' | 'today' | 'last7days' | 'month' | 'custom';

export interface BillingDateRange {
  preset: BillingDatePreset;
  startDate: string;
  endDate: string;
}

export interface BillingDateRangeBounds {
  startAt?: string;
  endAt?: string;
}

const DATE_PRESET_LABELS: Record<BillingDatePreset, string> = {
  all: '全部',
  today: '今天',
  last7days: '近 7 天',
  month: '本月',
  custom: '自定义',
};

export const BILLING_DATE_PRESETS: BillingDatePreset[] = [
  'all',
  'today',
  'last7days',
  'month',
  'custom',
];

export function getBillingDatePresetLabel(preset: BillingDatePreset): string {
  return DATE_PRESET_LABELS[preset];
}

function toDateInputValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function parseDateInputValue(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  return new Date(year, month - 1, day);
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

export function createBillingDateRange(
  preset: BillingDatePreset = 'last7days',
  now: Date = new Date(),
): BillingDateRange {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  if (preset === 'all') {
    return { preset, startDate: '', endDate: '' };
  }
  if (preset === 'today') {
    const value = toDateInputValue(today);
    return { preset, startDate: value, endDate: value };
  }
  if (preset === 'month') {
    return {
      preset,
      startDate: toDateInputValue(new Date(today.getFullYear(), today.getMonth(), 1)),
      endDate: toDateInputValue(today),
    };
  }

  return {
    preset,
    startDate: toDateInputValue(addDays(today, -6)),
    endDate: toDateInputValue(today),
  };
}

export function getBillingDateRangeBounds(range: BillingDateRange): BillingDateRangeBounds {
  if (range.preset === 'all' || !range.startDate || !range.endDate) {
    return {};
  }
  const endDate = parseDateInputValue(range.endDate);
  if (!endDate) {
    return {};
  }
  return {
    startAt: `${range.startDate}T00:00:00`,
    endAt: `${toDateInputValue(addDays(endDate, 1))}T00:00:00`,
  };
}

export function buildBillingUsageQuery(range: BillingDateRange): string {
  const bounds = getBillingDateRangeBounds(range);
  if (!bounds.startAt || !bounds.endAt) {
    return '';
  }
  const params = new URLSearchParams({
    start_at: bounds.startAt,
    end_at: bounds.endAt,
  });
  return params.toString();
}

function costToBillingPoints(cost: number): number {
  if (!Number.isFinite(cost) || cost <= 0) {
    return 0;
  }
  return Math.ceil(cost * POINTS_PER_YUAN);
}

function aggregateBillingCostToPoints(totalCost: number, taskCount: number): number {
  const normalizedTaskCount = Number.isFinite(taskCount) ? Math.max(0, Math.trunc(taskCount)) : 0;
  if (normalizedTaskCount <= 0) {
    return costToBillingPoints(totalCost);
  }
  return costToBillingPoints(totalCost / normalizedTaskCount) * normalizedTaskCount;
}

export function formatPointCount(points: number): string {
  const normalized = Number.isFinite(points) ? Math.max(0, Math.trunc(points)) : 0;
  return String(normalized);
}

export function formatBillingPoints(cost: number, taskCount?: number): string {
  const points =
    taskCount === undefined
      ? costToBillingPoints(cost)
      : aggregateBillingCostToPoints(cost, taskCount);
  return formatPointCount(points);
}

export function getBillingModelPoints(model: { total_cost: number; task_count: number }): number {
  return aggregateBillingCostToPoints(model.total_cost, model.task_count);
}

export function getMediaBillingPoints(input: {
  image_cost: number;
  image_task_count: number;
  video_cost: number;
  video_task_count: number;
}): number {
  return (
    aggregateBillingCostToPoints(input.image_cost, input.image_task_count) +
    aggregateBillingCostToPoints(input.video_cost, input.video_task_count)
  );
}

function getGlobalBillingTotalPoints(usage: GlobalBillingUsage): number {
  const mediaPoints = getMediaBillingPoints(usage);
  if (mediaPoints > 0) {
    return mediaPoints;
  }
  return aggregateBillingCostToPoints(usage.total_cost, usage.billed_task_count);
}

export function formatBillingCategory(category: string): string {
  return CATEGORY_LABELS[category] ?? category;
}

export function formatBillingTime(value: string): string {
  if (!value) {
    return '-';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hour = String(date.getHours()).padStart(2, '0');
  const minute = String(date.getMinutes()).padStart(2, '0');
  return `${year}/${month}/${day} ${hour}:${minute}`;
}

export function getBillingSummaryItems(usage: BillingUsage): BillingSummaryItem[] {
  return [
    {
      label: '任务数',
      value: String(usage.billed_task_count),
      caption: '模型任务记录',
      emphasized: true,
    },
    {
      label: '模型数',
      value: String(usage.models.length),
      caption: '使用过的模型',
    },
  ];
}

export function getGlobalBillingSummaryItems(usage: GlobalBillingUsage): BillingSummaryItem[] {
  return [
    {
      label: '总积分',
      value: formatPointCount(getGlobalBillingTotalPoints(usage)),
      caption: '图片与视频积分合计',
      emphasized: true,
    },
    {
      label: '用户数',
      value: String(usage.active_user_count),
      caption: '使用积分的账号',
    },
    {
      label: '模型数',
      value: String(usage.models.length),
      caption: '使用积分的模型',
    },
    {
      label: '生图次数',
      value: String(usage.image_task_count),
      caption: `使用 ${formatBillingPoints(usage.image_cost, usage.image_task_count)} 积分`,
    },
    {
      label: '生视频次数',
      value: String(usage.video_task_count),
      caption: `使用 ${formatBillingPoints(usage.video_cost, usage.video_task_count)} 积分`,
    },
  ];
}

export function getRecentTasksHeading(): { title: string; detail: string } {
  return {
    title: '最近任务',
    detail: '展示最近 20 条模型任务',
  };
}
